<template>
  <AppFormGroup>
    <SubHeading keypath="headings.income" />

    <div>
      <AppField
        v-model:visible="netMonthlyCentsVisible"
        :path-prefix="applicantType"
        path="income.netMonthlyCents"
      />

      <AppField
        v-model:visible="grossMonthlyCentsVisible"
        :path-prefix="applicantType"
        path="income.grossMonthlyCents"
      />

      <AppField
        v-model:visible="grossAnnualCentsVisible"
        :path-prefix="applicantType"
        path="income.grossAnnualCents"
      />

      <DisclosureDictionary
        v-if="
          netMonthlyCentsVisible ||
          grossMonthlyCentsVisible ||
          grossAnnualCentsVisible
        "
        type="income"
        class="px-1 text-sm"
        :provider-key="providerKey"
        vertical="retail"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <AppField :path-prefix="applicantType" path="income.type" />
      <AppField
        :path-prefix="applicantType"
        path="income.directDeposit"
        preferred-component="yes-no"
      />
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 md:gap-4">
      <AppField
        v-model="payFrequency"
        :path-prefix="applicantType"
        path="income.payFrequency"
      />
      <AppField
        v-model="previousPayDate"
        :path-prefix="applicantType"
        path="income.previousPayDate"
      />
      <AppField
        v-model="nextPayDate"
        :path-prefix="applicantType"
        path="income.nextPayDate"
      />
    </div>
  </AppFormGroup>
</template>

<script setup lang="ts">
  import {
    AppField,
    AppFormGroup,
  } from "@versatile-credit-apply/components/form"

  import { DisclosureDictionary } from "@versatile-credit-apply/components"
  import { ref, toValue, watch } from "vue"
  import type { FormSectionProps } from "./form-section-api"
  import type { PayFrequency } from "@versatile-credit-apply/omni-apply-client"
  import {
    calculateNextPayDate,
    useProviderI18n,
  } from "@versatile-credit-apply/sdk"

  const props = defineProps<FormSectionProps>()

  useProviderI18n(() => props.providerKey)

  const netMonthlyCentsVisible = ref<boolean>()
  const grossMonthlyCentsVisible = ref<boolean>()
  const grossAnnualCentsVisible = ref<boolean>()

  const nextPayDate = ref<string>()
  const payFrequency = ref<PayFrequency>()
  const previousPayDate = ref<string>()

  watch(
    [payFrequency, previousPayDate],
    ([newPayFrequency, newPreviousPayDate]) => {
      const newNextPayDate = toValue(nextPayDate)

      if (
        newPayFrequency == undefined ||
        newPreviousPayDate == undefined ||
        newNextPayDate != undefined
      ) {
        return
      }

      nextPayDate.value = calculateNextPayDate(
        newPayFrequency,
        newPreviousPayDate,
        "isoDate"
      )
    }
  )
</script>

<i18n lang="yaml" locale="en">
appFields:
  directDeposit:
    label: Direct Deposit
  nextPayDate:
    label: Next Pay Date
  previousPayDate:
    label: Previous Pay Date
  primaryIncomeType:
    label: Income Type
headings:
  employer: Employer Information
  income: Income Information
</i18n>

<i18n lang="yaml" locale="aff_standard_en">
appFields:
  previousPayDate:
    description: Only with Twice a Month
</i18n>

<i18n lang="yaml" locale="progressive_standard_en">
appFields:
  netMonthlyCents:
    label: Monthly Income
</i18n>

<i18n lang="yaml" locale="snap_standard_en">
appFields:
  grossMonthlyCents:
    label: Paycheck Amount
</i18n>
